import { useEffect } from 'react';

/**
 * Performance optimizer for landing pages
 * Applies various performance optimizations automatically
 */
export const PerformanceOptimizer = () => {
  useEffect(() => {
    // Optimize images after page load
    const optimizeImages = () => {
      const images = document.querySelectorAll('img[loading="lazy"]');
      
      const imageObserver = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const img = entry.target as HTMLImageElement;
            img.classList.add('loaded');
            imageObserver.unobserve(img);
          }
        });
      }, {
        rootMargin: '50px'
      });

      images.forEach((img) => imageObserver.observe(img));
    };

    // Prefetch critical resources on idle
    const prefetchCriticalResources = () => {
      if ('requestIdleCallback' in window) {
        requestIdleCallback(() => {
          // Prefetch next page resources
          const criticalUrls = [
            '/chateaux',
            '/blog',
            '/faq'
          ];

          criticalUrls.forEach(url => {
            const link = document.createElement('link');
            link.rel = 'prefetch';
            link.href = url;
            document.head.appendChild(link);
          });
        });
      }
    };

    // Optimize third-party scripts loading
    const optimizeThirdPartyScripts = () => {
      // Defer non-critical analytics scripts
      setTimeout(() => {
        if (window.gtag && !window.dataLayer?.length) {
          console.log('Analytics loading deferred for performance');
        }
      }, 3000);
    };

    // Apply optimizations after DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        optimizeImages();
        prefetchCriticalResources();
        optimizeThirdPartyScripts();
      });
    } else {
      optimizeImages();
      prefetchCriticalResources();
      optimizeThirdPartyScripts();
    }

    // Cleanup function
    return () => {
      // Observer cleanup is handled automatically when components unmount
    };
  }, []);

  return null; // This component doesn't render anything
};

// Web Vitals reporting utility
export const reportWebVitals = (metric: any) => {
  if (window.gtag) {
    window.gtag('event', 'web_vitals', {
      custom_map: { metric_id: 'dimension1' },
      metric_id: metric.id,
      metric_value: Math.round(metric.name === 'CLS' ? metric.value * 1000 : metric.value),
      metric_delta: Math.round(metric.name === 'CLS' ? metric.delta * 1000 : metric.delta),
    });
  }
};